import React, { useEffect, useState } from 'react';
import './SeoEnricherSections.css';
import { SeoEnricherSection } from '../../utils/seoEnricherPreview';

type SeoEnricherSectionsProps = {
  sourceCityName?: string;
  destinationCityName?: string;
  /**
   * Pre-resolved sections (token replacement / source selection happens in
   * the parent, e.g. RouteTemplate).
   */
  sections?: SeoEnricherSection[];
};

// Accent palette (mirrors RouteInfoTabs colors), cycled across items.
const ACCENTS = [
  '#d97706',
  '#2563eb',
  '#7c3aed',
  '#dc6b30',
  '#0d9488',
  '#0c58b4',
  '#db2777',
  '#16a34a'
];

const SeoEnricherSections = ({
  sourceCityName,
  destinationCityName,
  sections
}: SeoEnricherSectionsProps) => {
  const previewSections = sections && sections.length ? sections : [];

  // On SSR (and the first hydration render) isClient is false, so all content
  // is rendered inline & visible. After mount it flips to false-by-default,
  // collapsing the content on the client (shown via modal on click).
  const [isClient, setIsClient] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!previewSections.length) return null;

  const title =
    sourceCityName && destinationCityName
      ? `${sourceCityName} to ${destinationCityName} Cab Booking Guide on MakeMyTrip`
      : 'Cab Booking Guide on MakeMyTrip';

  const activeSection =
    activeIdx !== null ? previewSections[activeIdx] : undefined;

  return (
    <section className="inclExcl">
      <div className="inclExcl__header">
        <h2 className="inclExcl__title">{title}</h2>
      </div>

      <div className="inclExcl__grid">
        {previewSections.map((section, idx) => {
          const accent = ACCENTS[idx % ACCENTS.length];
          return (
            <div
              className="inclExcl__item"
              style={{ borderLeftColor: accent }}
              key={`item-${idx}`}
            >
              <div className="inclExcl__itemMain">
                <div className="inclExcl__itemBody">
                  <p className="inclExcl__itemTitle">{section.title}</p>
                  <p className="inclExcl__itemSubtitle">{section.subtitle}</p>
                </div>
                <button
                  type="button"
                  className="inclExcl__infoBtn"
                  style={{ borderColor: accent, color: accent }}
                  aria-label={`More about ${section.title}`}
                  onClick={() => setActiveIdx(idx)}
                >
                  i
                </button>
              </div>
              {/* SSR / pre-hydration: content is rendered inline for SEO. */}
              {!isClient && section.content ? (
                <div
                  className="inclExcl__infoBox"
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Client-only: clicking the info icon opens the content in a modal box. */}
      {isClient && activeSection && activeSection.content ? (
        <div
          className="inclExcl__modalOverlay"
          onClick={() => setActiveIdx(null)}
        >
          <div
            className="inclExcl__modal"
            role="dialog"
            aria-modal="true"
            onClick={e => e.stopPropagation()}
          >
            <div className="inclExcl__modalHeader">
              <div>
                <h3 className="inclExcl__modalTitle">{activeSection.title}</h3>
                {activeSection.subtitle ? (
                  <p className="inclExcl__modalSubtitle">
                    {activeSection.subtitle}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                className="inclExcl__modalClose"
                aria-label="Close"
                onClick={() => setActiveIdx(null)}
              >
                ×
              </button>
            </div>
            <div
              className="inclExcl__modalBody"
              dangerouslySetInnerHTML={{ __html: activeSection.content }}
            />
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default SeoEnricherSections;
